/**
 * @file: useFeedThreadMessages.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Paginated message list for one thread.
 */

import { FeedInboxItem } from '@euler/model/feed';
import { FeedMessageType } from '@euler/model/feed/FeedMessageType';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';

const kPageSize = 30;

export interface UseFeedThreadMessagesResult {
  messages: FeedInboxItem[];
  loading: boolean;
  refreshing: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: Error | undefined;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  /**
   * Mark a single message read (by its recipient-row id). Optimistically
   * sets the item's `readAt` so the card updates immediately; the server
   * call is best-effort and idempotent.
   */
  markRead: (recipientId: string) => Promise<void>;
  /**
   * Flip every loaded maintenance-plan reminder for the given plan details to
   * read — used after converting a bundle of details into a lead, so the whole
   * bundle clears at once instead of only the tapped card. Local only: the
   * conversion itself already issued the server write (see
   * `LeadAffordanceButton`), which also covers rows that are paged out.
   */
  applyRemindersRead: (planDetailIds: number[]) => void;
}

export const useFeedThreadMessages = (
  threadId: string,
): UseFeedThreadMessagesResult => {
  const { feedService } = useServiceFactory();
  const [messages, setMessages] = useState<FeedInboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<Error | undefined>(undefined);
  const reqRef = useRef(0);

  const fetchPage = useCallback(
    async (cursor: string | undefined) => {
      const reqId = ++reqRef.current;
      const res = await feedService.listMessagesInThread(threadId, {
        cursor,
        limit: kPageSize,
      });
      if (reqId !== reqRef.current) return null;
      return res;
    },
    [feedService, threadId],
  );

  const refresh = usePersistFn(async () => {
    setRefreshing(true);
    setError(undefined);
    try {
      const res = await fetchPage(undefined);
      if (res) {
        setMessages(res.items);
        setNextCursor(res.nextCursor);
      }
    } catch (e) {
      setError(e as Error);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  });

  const loadMore = usePersistFn(async () => {
    if (loadingMore || nextCursor == null) return;
    setLoadingMore(true);
    try {
      const res = await fetchPage(nextCursor);
      if (res) {
        setMessages(prev => [...prev, ...res.items]);
        setNextCursor(res.nextCursor);
      }
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoadingMore(false);
    }
  });

  const markRead = usePersistFn(async (recipientId: string) => {
    let wasUnread = false;
    setMessages(prev =>
      prev.map(m => {
        if (m.id !== recipientId || m.readAt) return m;
        wasUnread = true;
        return { ...m, readAt: new Date().toISOString() };
      }),
    );
    if (!wasUnread) return;
    // Best-effort + idempotent; a failure just leaves the optimistic
    // read state, which the next refresh reconciles. Don't surface it as
    // a list-level error (the list itself is fine).
    try {
      await feedService.markMessageRead(recipientId);
    } catch (e) {
      console.error('markMessageRead failed', e);
    }
  });

  const applyRemindersRead = usePersistFn((planDetailIds: number[]) => {
    const ids = new Set(planDetailIds);
    setMessages(prev =>
      prev.map(m => {
        if (
          m.readAt ||
          m.params.type !== FeedMessageType.OrgMaintenancePlanRemind ||
          !ids.has(m.params.refs.planDetailId)
        ) {
          return m;
        }
        return { ...m, readAt: new Date().toISOString() };
      }),
    );
  });

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    messages,
    loading,
    refreshing,
    loadingMore,
    hasMore: nextCursor != null,
    error,
    refresh,
    loadMore,
    markRead,
    applyRemindersRead,
  };
};
